import {
  Cell,
  ColumnDef,
  flexRender,
  Header,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo } from 'react';

import { Table } from '../components/Table';
import { TablePaginationConfig } from '../types';
import { useActionColumnAutoConfig } from './useActionColumnAutoConfig';
import { useCheckboxColumn } from './useCheckboxColumn';
import { useTableStore } from './useTableStore';

interface UseTableSetupOptions<T extends Record<string, any>> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  alwaysShowCheckbox?: boolean;
  getGroupId?: (data: T) => string | null | undefined;
  getItemId?: (data: T) => string;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For hierarchical data
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  pagination?: TablePaginationConfig;
  summaryData?: T | null;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  getSubRows?: (data: T) => T[]; // For hierarchical data
  isGroupRow?: (data: T) => boolean; // For identifying expandable rows
  getRowCanExpand?: (row: { original: T }) => boolean; // For hierarchical data
  manualPagination?: boolean;
  rowCount?: number;
}

export const useTableSetup = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  alwaysShowCheckbox,
  getGroupId,
  getItemId,
  getRowId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  onFilteredRowsChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  pagination,
  summaryData,
  state,
  sortingFns,
  meta,
  getSubRows,
  isGroupRow,
  getRowCanExpand,
  manualPagination,
  rowCount,
}: UseTableSetupOptions<T>) => {
  const { showCheckbox, checkboxColumn } = useCheckboxColumn({
    data,
    alwaysShowCheckbox,
    showSelectAllCheckbox: meta?.showSelectAllCheckbox,
    getGroupId,
    getItemId,
    isSelectionDisabled,
  });

  // Build columns array with checkbox if needed
  const tableColumns = useMemo(() => {
    if (showCheckbox) {
      return [checkboxColumn, ...columns];
    }
    return columns;
  }, [columns, showCheckbox, checkboxColumn]);

  const table = useTableStore({
    data,
    columns: tableColumns,
    enableMultiSort: true,
    enableRowSelection,
    enableMultiRowSelection,
    enableSubRowSelection,
    pagination,
    summaryData,
    state,
    sortingFns,
    onRowSelectionChange,
    onExpandedChange,
    getSubRows,
    getRowId,
    getRowCanExpand:
      getRowCanExpand ??
      (isGroupRow ? (row) => Boolean(isGroupRow(row.original)) : undefined),
    onPaginationChange,
    onSortingChange,
    manualPagination,
    rowCount,
    meta: {
      ...meta,
    },
  });

  const { ActionCellWrapper } = useActionColumnAutoConfig(table, {
    actionColId,
  });

  const renderRowCell = useCallback(
    (cell: Cell<T, unknown>) => {
      return (
        <Table.Cell
          key={cell.id}
          columnId={cell.column.id}
          align={cell.column.columnDef.meta?.align}
          cellContext={cell}
        >
          {cell.column.id === actionColId ? (
            <ActionCellWrapper rowId={cell.row.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </ActionCellWrapper>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </Table.Cell>
      );
    },
    [ActionCellWrapper, actionColId]
  );

  // Shared header renderer
  const renderHeader = useCallback(
    (header: Header<T, unknown>, column: ColumnDef<T> | undefined) => {
      return (
        <Table.ColumnHeader
          key={header.id}
          columnId={header.column.id}
          align={column?.meta?.align}
          sortable={header.column.getCanSort()}
          headerContext={header}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </Table.ColumnHeader>
      );
    },
    []
  );

  const renderRowContent = useCallback(
    (row: Row<T>) => {
      return <>{row.getVisibleCells().map((cell) => renderRowCell(cell))}</>;
    },
    [renderRowCell]
  );

  const renderHeaderGroups = useCallback(() => {
    return table.getHeaderGroups().map((headerGroup) =>
      headerGroup.headers.map((header) => {
        const column = columns.find((col) => col.id === header.column.id);
        return renderHeader(header, column);
      })
    );
  }, [table, columns, renderHeader]);

  const renderFooter = useCallback(() => {
    return <Table.Footer />;
  }, []);

  const renderFooterRows = useCallback(() => {
    const hasFooter = table
      .getFooterGroups()
      .some((group) =>
        group.headers.some((header) => header.column.columnDef.footer)
      );
    const hasSummaryData = (table.options.meta as any)?.summaryData != null;

    if (!hasFooter || !hasSummaryData) return null;

    return table.getFooterGroups().map((footerGroup) => (
      <tr key={footerGroup.id}>
        {footerGroup.headers.map((header) => {
          const tanstackColumn = header.column;

          return (
            <Table.FooterCell key={header.id}>
              {flexRender(header.column.columnDef.footer, header.getContext())}
            </Table.FooterCell>
          );
        })}
      </tr>
    ));
  }, [table]);

  // Trigger onFilteredRowsChange when column filters change
  useEffect(() => {
    if (onFilteredRowsChange) {
      const filteredRows = table.getFilteredRowModel().flatRows;
      onFilteredRowsChange(filteredRows as Row<T | null>[] | undefined);
    }
  }, [table.getState().columnFilters, onFilteredRowsChange, table]);

  return {
    table,
    tableColumns,
    renderRowCell,
    renderHeader,
    renderRowContent,
    renderHeaderGroups,
    renderFooter,
    renderFooterRows,
    ActionCellWrapper,
    showCheckbox,
  };
};
